/**
 * Every message that crosses the wire, in one file.
 *
 * `docs/DESIGN.md`, "Netcode contract": "Clients do not stream board state.
 * They send input events with frame numbers, plus a board hash every N
 * pieces." Nothing here carries a board from a client. The only board that
 * ever moves is a *server* snapshot, sent one way, to repair a client the
 * server has already caught diverging.
 *
 * Two conventions hold throughout:
 *
 * - **Every tick number is absolute**, counted from the first tick of the
 *   current round. Client and server both start a round at tick 0, so an
 *   input's `frame`, a garbage batch's `queueAtTick` and a relay's `toTick`
 *   all mean the same instant on both sides without any clock synchronisation.
 * - **The server never asks a client what it cleared.** `LockResult.attackSent`
 *   exists in the engine, but no message carries it from a client. Attack is
 *   computed by the server's own replay (BASELINE invariant 6).
 */
import type { InputAction } from '@interlock/engine';
import type { CurseWindow, GarbageSchedule, StateSnapshot } from '@interlock/sim';

import type { AiLevel } from './ai.js';
import { isCurseLoadout } from './curses.js';
import type { CurseLoadout } from './curses.js';
import { CURSE_LOADOUT_SIZE } from './limits.js';
import type { ClientRole, MatchType, Seat, SeatPresence } from './lobby.js';
import type { DuelRules } from './variants.js';
import type { RefusalReason } from './version.js';

/* ------------------------------------------------------------------ *
 * Message type names
 * ------------------------------------------------------------------ */

/** Everything a client is allowed to send. Anything else is dropped and logged. */
export const C2S = {
  /** Toggle this player's ready flag in the lobby. */
  ready: 'ready',
  /**
   * Pick a curse loadout, in the lobby, before the match. `docs/DESIGN.md`,
   * "curses": "Players pick three before the match." Accepted in every room —
   * a loadout picked in a room that never plays `curses` is simply never
   * read, the same way a `duelSprint` goal on a classic-duel room would be —
   * so this needs no variant check to be safe, only one to be *meaningful*.
   */
  loadout: 'loadout',
  /** A batch of input events plus the frame the client has reached. */
  input: 'input',
  /** A board hash at a piece-count checkpoint. */
  hash: 'hash',
  /** Ask for another match after this one ended. */
  rematch: 'rematch',
  /**
   * "My page is going to the background" / "I am back."
   *
   * `docs/DESIGN.md`, PWA requirements: "Pause on `visibilitychange`, and tell
   * the server before the socket dies." iOS freezes a backgrounded page within
   * seconds, and the socket dies with it — with no close frame and no warning.
   * Sending this on the way out is the difference between a player who
   * backgrounded the app and a player who rage quit, and it has to be sent
   * *before* the freeze because afterwards there is nothing left running to
   * send it.
   */
  pause: 'pause',
  /**
   * The client half of the heartbeat.
   *
   * A WiFi-to-mobile handoff drops the socket silently: no close frame, no
   * error, both ends still believing they are connected. A reply that stops
   * coming back is the only evidence of that, so the client asks for one on a
   * fixed interval and gives up on the socket when the answers stop.
   */
  ping: 'ping',
} as const;

export type C2SType = (typeof C2S)[keyof typeof C2S];

/** Everything the server sends. */
export const S2C = {
  /** First message after a successful join: who you are and where you landed. */
  welcome: 'welcome',
  /** Lobby and match bookkeeping: phase, names, ready flags, round score. */
  room: 'room',
  /** A round is about to begin. Carries the seed — the server owns it. */
  roundStart: 'roundStart',
  /** The authoritative per-seat readout the HUD and the incoming meter read. */
  state: 'state',
  /**
   * Validated inputs and scheduled garbage for one seat, as the server's own
   * simulation consumed them. The seat's owner uses the garbage; everyone else
   * uses both, to mirror that seat's board.
   */
  sync: 'sync',
  /** A server snapshot, after the server caught this client's hash diverging. */
  resync: 'resync',
  /**
   * The seat is yours again: here is where the authoritative simulation got to,
   * and here is the opponent's board as the server has it.
   *
   * The rejoin counterpart of `resync`, and deliberately the same *shape* of
   * event — a snapshot plus a new epoch — because it closes the same race for
   * the same reason. What it adds is the other seat's board: a returning
   * client's mirror cannot be replayed from a relay it never received.
   */
  resume: 'resume',
  /**
   * Everybody is back: play resumes at this tick, in this many milliseconds.
   *
   * `docs/DESIGN.md`'s amendment of 2026-08-24: "the server announces a resume
   * tick and both clients show a 3-second countdown, then play resumes
   * together." Broadcast, not per-seat — the whole point is that every screen
   * in the room, players and spectators alike, counts the same three numbers
   * down and starts on the same instant. A `resume` (the snapshot) may come
   * with it for whichever seat was away; this one comes to everybody.
   */
  resumeAt: 'resumeAt',
  /** A round ended. */
  roundEnd: 'roundEnd',
  /** The match ended. */
  matchEnd: 'matchEnd',
  /** A message was dropped: rate limit, spectator, or wrong phase. */
  warning: 'warning',
  /** The join was refused, or the client is being disconnected. */
  refused: 'refused',
  /** The heartbeat's answer. Its only content is that it arrived. */
  pong: 'pong',
} as const;

export type S2CType = (typeof S2C)[keyof typeof S2C];

/* ------------------------------------------------------------------ *
 * Join
 * ------------------------------------------------------------------ */

/**
 * Sent as Colyseus join options rather than as a message, because the version
 * check has to happen before the client is in the room at all — a peer that
 * speaks the wrong protocol must never reach a state where it could send an
 * input.
 */
export interface JoinOptions {
  readonly protocolVersion: number;
  readonly name: string;
  readonly role: ClientRole;
  /**
   * The token a previous `welcome` minted for this seat, when this join is a
   * *rejoin*. `docs/DESIGN.md`: "Rejoin uses a session token, so a refresh or a
   * WiFi-to-mobile handoff is not a forfeit."
   *
   * There are two rejoin paths and this is the second one. The fast path is the
   * transport's own reconnection token, which reattaches to a seat the room is
   * still holding — it is what a ten-second outage uses, and no join happens at
   * all. This one is the path for when there is nothing left to reattach to:
   * the server restarted and rebuilt the room from a checkpoint, so a *fresh*
   * join has to be able to say which seat it is. Absent, it is an ordinary
   * join and the seat is allocated normally.
   */
  readonly sessionToken?: string;
  /**
   * A **socket ticket** from `POST /api/auth/ticket`, when the player is
   * signed in. `docs/DESIGN.md`'s accounts amendment: "room join options may
   * carry the session token so the server can attribute seats to players
   * (never trust a client-claimed username — resolve it server-side from the
   * session)."
   *
   * Three things it is not, and each of them is deliberate:
   *
   * - **Not `sessionToken`.** That field is the *seat* token above, minted by
   *   the room, and the two have nothing to do with each other. Two names
   *   because there are two concepts; a duel rejoin and a signed-in player are
   *   independent (you can be either, both or neither).
   * - **Not the login cookie.** The cookie is `httpOnly`, so page JavaScript
   *   cannot read it to put it here — which is the whole point of it being
   *   `httpOnly`. This is a separate single-use token that lives sixty
   *   seconds. `docs/decisions/0028`.
   * - **Not an identity claim.** The server redeems the ticket and reads the
   *   username off the row it names. `name` above stays what it always was: a
   *   display name the player may edit per room, sanitised on arrival, and
   *   never treated as who anybody is.
   *
   * Absent — a guest, an offline install, a deployment with no database — the
   * join is exactly the join it was before accounts existed. That is why
   * `PROTOCOL_VERSION` did not move: see `version.ts`.
   */
  readonly accountToken?: string;
}

/* ------------------------------------------------------------------ *
 * Client to server
 * ------------------------------------------------------------------ */

/**
 * One input event on the wire.
 *
 * `slot` addresses one of a shared well's two active pieces, and it travels in
 * exactly one direction: **outbound, on a relay the server stamped.** A duel
 * has one piece per player and omits it entirely.
 *
 * `parseInputMessage` never reads a client's `slot` and never will — it
 * rebuilds each event as `{ frame, action }`. A seat's slot is *the seat*, and
 * the server knows which seat a socket is sitting in; taking the number from
 * the message instead would let one player move the other player's piece, which
 * is the co-op shape of "never trust a client's claim" (BASELINE invariant 6).
 */
export interface WireInput {
  readonly frame: number;
  readonly action: InputAction;
  /** Which active piece, in a shared well. Server-stamped, outbound only. */
  readonly slot?: number;
}

export interface ReadyMessage {
  readonly ready: boolean;
}

/** A curse loadout: exactly `CURSE_LOADOUT_SIZE` distinct effects. */
export interface LoadoutMessage {
  readonly effects: CurseLoadout;
}

/**
 * Which run of the round a client's message belongs to.
 *
 * A round starts at epoch 0. Every resync increments it, and the server
 * ignores anything stamped with an older one.
 *
 * This is not bookkeeping, it closes a race. When the server sends a resync it
 * rewinds the client to a tick the client has already passed, and the client
 * re-decides what to do from there. But the client had already *sent* inputs
 * for the frames in between, and those are still in flight. Without an epoch
 * the server would accept them, replay a history the repaired client is not
 * going to reproduce, and fail the very next checkpoint — for a reason that
 * has nothing to do with the client's honesty, and with a probability that
 * grows with latency.
 */
export type Epoch = number;

/**
 * "Here is everything I did up to and including frame `frame`."
 *
 * `frame` is what makes the server's replay safe: the server never advances a
 * player's simulation past the frame that player has reported, so an event can
 * never arrive for a tick the server has already run. Ordered delivery over
 * one socket does the rest.
 */
export interface InputMessage {
  readonly frame: number;
  readonly events: readonly WireInput[];
  readonly epoch: Epoch;
}

/**
 * A checkpoint: "at the moment my `piecesPlaced` reached `piecesPlaced`, my
 * state hashed to `hash`." Neither end decides when that moment is:
 * `@interlock/sim`'s `SimDriver` produces the checkpoint on both, at the end
 * of the tick a multiple of `HASH_EVERY_N_PIECES` was crossed, and that is
 * what keeps the two from drifting apart.
 */
export interface HashMessage {
  readonly piecesPlaced: number;
  readonly hash: number;
  readonly epoch: Epoch;
}

export type RematchMessage = Record<string, never>;

/** "My page went to the background" (`true`) or "I am back" (`false`). */
export interface PauseMessage {
  readonly paused: boolean;
}

/**
 * A heartbeat. `t` is the client's own clock, echoed back untouched — the
 * server neither reads it nor trusts it, and it exists only so a client can
 * measure a round trip if it ever wants to.
 */
export interface PingMessage {
  readonly t: number;
}

/* ------------------------------------------------------------------ *
 * Server to client
 * ------------------------------------------------------------------ */

export interface WelcomeMessage {
  readonly protocolVersion: number;
  readonly code: string;
  readonly role: ClientRole;
  /** `null` for a spectator. */
  readonly seat: Seat | null;
  /**
   * The seat's identity across sockets. Stable for the life of the seat, and
   * what a rejoin presents to claim it back — see `JoinOptions.sessionToken`.
   */
  readonly sessionToken: string;
  /**
   * True when this `welcome` is the answer to a rejoin: the seat was held (or
   * restored from a checkpoint) and has been handed back rather than allocated.
   * A `resume` follows.
   */
  readonly resumed: boolean;
}

/**
 * `frozen` is phase 05's addition. `docs/DESIGN.md`: "On grace expiry: friendly
 * freezes and stays resumable." It is not an ending — no winner is decided, no
 * score changes, and every board is exactly where it was. Both clocks stop, and
 * the match picks up from the same tick when the missing player comes back.
 *
 * `paused` and `resuming` are owner overhaul part 9's, and they are the same
 * stopped clock at two other moments. `docs/DESIGN.md`'s amendment of
 * 2026-08-24: "When a seat drops unconsented (or announces a pause), the WHOLE
 * match freezes for every player."
 *
 * - `paused` — a seat is away and is still being waited for. Every board is
 *   stopped, the match clock is stopped, nothing lands and nothing expires.
 *   Which seat, and how long is left, is `PlayerView.presence` and
 *   `PlayerView.graceMsLeft`/`pauseMsLeft` — this phase says only that the
 *   match itself has stopped.
 * - `frozen` — the same stopped clock after the grace window ran out on a
 *   friendly match. The difference from `paused` is that nothing is counting
 *   down any more: the seats are held until somebody comes back or
 *   `FROZEN_MATCH_TTL_MS` collects the room.
 * - `resuming` — everybody is back and the 3-2-1 is running. `RoomMessage.
 *   timerMs` counts it down for a peer that joined mid-countdown; `resumeAt`
 *   is the announcement everybody already in the room renders it from.
 *
 * All three are *stopped*: no simulation advances, and the time spent in them
 * is subtracted from the played time the authoritative ceiling is measured
 * against (`docs/decisions/0019`, `0041`).
 */
export type MatchPhase =
  'lobby' | 'countdown' | 'playing' | 'roundOver' | 'matchOver' | 'paused' | 'resuming' | 'frozen';

/** True while the match has stopped for everyone and no board is advancing. */
export function isStoppedPhase(phase: MatchPhase): boolean {
  return phase === 'paused' || phase === 'resuming' || phase === 'frozen';
}

export interface PlayerView {
  readonly seat: Seat;
  readonly name: string;
  readonly ready: boolean;
  readonly connected: boolean;
  readonly roundsWon: number;
  readonly wantsRematch: boolean;
  /**
   * Where this seat's occupant is. Broadcast for *both* seats, because the
   * player who is still connected has as much right to know that their opponent
   * is reconnecting as the reconnecting player has to see their own countdown.
   */
  readonly presence: SeatPresence;
  /** Milliseconds left in this seat's grace window; `null` when it is not counting. */
  readonly graceMsLeft: number | null;
  /**
   * Milliseconds of **consented pause** this seat has left in the current
   * round, or `null` outside a round.
   *
   * A paused seat stops the whole match (`MatchPhase`'s `paused`), so the
   * amount of it one player may spend has to be bounded or a player losing a
   * round could hold the other one hostage by toggling `visibilitychange`.
   * `PAUSE_BUDGET_MS` is the bound; this is what is left of it, broadcast for
   * *both* seats for the same reason `graceMsLeft` is — the player who is
   * waiting has as much right to know how long the wait can last as the player
   * who is spending it. A budget nobody can see is a rule that feels like a
   * bug when it fires.
   */
  readonly pauseMsLeft: number | null;
  /**
   * The curse loadout this seat has picked, or `null` before it has picked
   * one. Shown to both players in the lobby — `docs/DESIGN.md` says nothing
   * about hiding it, and there is no bluffing mechanic here for hiding it to
   * serve.
   */
  readonly curseLoadout: CurseLoadout | null;
  /**
   * True when this round's gravity and incoming attack are being eased for
   * this seat. `docs/prompts/07-specials.md`: "keep multipliers modest and
   * displayed — hidden rubber-banding feels like cheating when discovered."
   * A flag rather than the numbers themselves: what a player needs to know is
   * that the game is helping them, not the exact percentage.
   */
  readonly handicapped: boolean;
  /**
   * The difficulty this seat's **AI opponent** is playing at, or `null` when a
   * person is sitting in it (which is every seat of every room that was not
   * created with an AI option).
   *
   * Owner overhaul part 11. `name` already says `AI — Hard`, and that is the
   * half no screen can miss; this is the half no screen can be *fooled* about.
   * `sanitiseDisplayName` would happily let a person call themselves
   * `AI — Hard`, so a UI that decided "is this a bot" by reading the name
   * would be trusting a string a player chose. It reads this instead — set by
   * the server from its own construction of the seat, never from anything on
   * the wire — so the badge means what it says.
   *
   * A field rather than a boolean because the level is worth showing: "you
   * lost to Easy" and "you lost to Expert" are different pieces of news.
   */
  readonly bot: AiLevel | null;
}

export interface RoomMessage {
  readonly code: string;
  readonly phase: MatchPhase;
  readonly players: readonly PlayerView[];
  readonly spectators: number;
  readonly round: number;
  readonly bestOf: number;
  /** Milliseconds left in a countdown or intermission; `null` outside them. */
  readonly timerMs: number | null;
  /** Which grace window and which grace-expiry outcome this room is playing under. */
  readonly matchType: MatchType;
  /**
   * Which duel this room is playing, and how it is configured. Fixed for the
   * life of the room — chosen by whoever created it — so this is what the
   * lobby and the duel HUD describe. `docs/DESIGN.md`'s owner amendment: "the
   * lobby and duel HUD display it."
   */
  readonly rules: DuelRules;
  /**
   * Whether the per-player handicap is on for this room. A room option,
   * chosen at creation and fixed for its life like `rules` is —
   * `docs/prompts/07-specials.md`: "make it a room option (on by default in
   * friendly, display in lobby, creator can turn it off)." `PlayerView.
   * handicapped` says whether it is currently *doing* anything for a given
   * seat; this says whether the room allows it to at all.
   */
  readonly handicapEnabled: boolean;
}

/**
 * The server owns the seed (BASELINE invariant 6). Both players get the same
 * one, so both wells draw the same pieces in the same order and neither can
 * claim the bag was unfair — and in `duelSprint` that is not merely fair, it
 * is the mode: "both players race the same piece sequence."
 */
export interface RoundStartMessage {
  readonly round: number;
  readonly seed: number;
  /** Milliseconds until tick 0. Clients count down and start their loop at zero. */
  readonly startsInMs: number;
  /**
   * The rules to build this round's boards with.
   *
   * Carried here for exactly the reason `seed` is: this message is the one
   * that says "make your boards now", and a peer must never have to remember
   * something it was told earlier in order to construct a board correctly.
   * The engine options and the per-tick rule both come out of it, and both
   * are inside the hash.
   */
  readonly rules: DuelRules;
  /**
   * Each seat's gravity handicap this round, indexed by seat, in thousandths:
   * `1000` is neutral and `920` is gravity eased by 8%. Always an integer —
   * it is a `SimDriver`'s `gravityPermille`, which goes into hashed state.
   * Carried here for the identical reason `rules` is: a peer builds
   * *both* boards from this message (its own and, in a duel, its mirror of
   * the opponent's), so it needs the other seat's factor as much as its own,
   * and it must never have to remember one from earlier. See
   * `docs/decisions/0033`.
   */
  readonly handicap: readonly [number, number];
}

/** One seat's authoritative readout. Everything the duel HUD shows comes from here. */
export interface SeatState {
  readonly seat: Seat;
  /** The server's own simulation tick for this seat. */
  readonly tick: number;
  readonly lines: number;
  readonly attack: number;
  /** Garbage lines queued against this seat and not yet landed — the incoming meter. */
  readonly incoming: number;
  readonly piecesPlaced: number;
  readonly toppedOut: boolean;
}

export interface StateMessage {
  readonly round: number;
  readonly seats: readonly SeatState[];
  /**
   * Milliseconds left on a `duelSprint` time limit, `null` for every other
   * variant and for a lines target.
   *
   * The server's own clock, broadcast rather than derived: the round is
   * decided when *this* number reaches zero (BASELINE invariant 6 — "the
   * server owns ... the clock"), so a client counting one down itself would
   * eventually show a different number from the one the match is judged by.
   */
  readonly timeLeftMs: number | null;
  /**
   * `tug`'s rope, `null` for every other variant. `docs/DESIGN.md`: "Rope
   * position lives in server state, mirrored to clients (like the incoming
   * meter)." Positive pulls toward seat 1, negative toward seat 0 — the same
   * sign convention `docs/decisions/0032` fixes once, in `ropePosition`, so
   * there is exactly one place "which way is which" is decided.
   */
  readonly rope: number | null;
}

/**
 * What the server's simulation of one seat did, forwarded to everyone.
 *
 * `docs/prompts/04-server-duel.md` leaves how the opponent's board reaches the
 * screen open. This is the answer (`docs/decisions/0013`): the server relays
 * the inputs it *itself validated and replayed*, and every other client feeds
 * them through the same engine. No board is streamed, and a mirror is
 * bit-identical to the server's own copy rather than merely similar.
 *
 * In a co-op room there is one board and it is relayed under **seat 0**: the
 * events carry the slot they belong to, so a second relay would be the same
 * stream told twice. `COOP_RELAY_SEAT` is that constant.
 */
export interface SyncMessage {
  readonly seat: Seat;
  /** The tick the server's simulation of this seat has reached. */
  readonly toTick: number;
  readonly events: readonly WireInput[];
  readonly garbage: readonly GarbageSchedule[];
  /**
   * Curse windows newly announced against this seat's board since the last
   * relay. `curses` only, and absent everywhere else — every other variant's
   * list stays empty for the life of the round. `SimDriver.scheduleCurses`
   * is what a receiver does with it.
   */
  readonly curses?: readonly CurseWindow[];
}

export interface ResyncMessage {
  readonly seat: Seat;
  readonly snapshot: StateSnapshot;
  /** Garbage the server has scheduled but not yet inserted at the snapshot's tick. */
  readonly scheduled: readonly GarbageSchedule[];
  /** Curse windows this board has not finished living through. `curses` only. */
  readonly curses?: readonly CurseWindow[];
  /** The epoch to stamp every subsequent message with. See `Epoch`. */
  readonly epoch: Epoch;
  readonly strike: number;
  readonly limit: number;
}

/** One seat's board as the server holds it, for a client that has to rebuild it. */
export interface SeatSnapshot {
  readonly seat: Seat;
  readonly snapshot: StateSnapshot;
  /** Garbage announced against that seat but not yet inserted at the snapshot's tick. */
  readonly scheduled: readonly GarbageSchedule[];
  /** Curse windows this board has not finished living through. `curses` only. */
  readonly curses?: readonly CurseWindow[];
}

/**
 * The rejoin message: everything a returning client needs to be in step again.
 *
 * `docs/DESIGN.md`: "On rejoin: authoritative snapshot, reconcile, then the
 * resume countdown."
 *
 * `snapshot` is the server's own copy of the seat at `fromTick` — exactly as
 * far as the server's replay got before the client went quiet, because the
 * server never advances a seat past the frame that seat has reported. **The
 * client takes it flat**, which is owner overhaul part 9's change and follows
 * from the match pausing: the rest of the room stopped when this seat went
 * away, so anything this client kept simulating afterwards happened while
 * nobody else was playing, and replaying it would be handing one player a lead
 * over a stopped board. Phase 05 re-derived a buffered history on top of this
 * snapshot instead (`docs/decisions/0016`); `docs/decisions/0041` retires that.
 *
 * `opponent` is here because a mirror cannot be reconstructed from relays that
 * were never received.
 *
 * A `resumeAt` follows, to everybody, and *that* is what starts play again.
 */
export interface ResumeMessage {
  readonly seat: Seat;
  readonly round: number;
  readonly seed: number;
  /** The rules to rebuild both boards with — here for the same reason `seed` is. */
  readonly rules: DuelRules;
  /** Each seat's gravity handicap this round, in thousandths — here for the same reason `rules` is. */
  readonly handicap: readonly [number, number];
  readonly phase: MatchPhase;
  /** The epoch to stamp every subsequent message with. Same fence as a resync. */
  readonly epoch: Epoch;
  /** The tick the server's replay of this seat reached. The snapshot is at this tick. */
  readonly fromTick: number;
  readonly snapshot: StateSnapshot;
  readonly scheduled: readonly GarbageSchedule[];
  /** Curse windows this board has not finished living through. `curses` only. */
  readonly curses?: readonly CurseWindow[];
  /**
   * The other seat's board. In co-op it is the *same* board — the snapshot
   * above, at the same tick — because there is only one, and a returning
   * client rebuilds from `snapshot` and ignores this.
   */
  readonly opponent: SeatSnapshot;
  readonly scores: readonly number[];
}

/**
 * "Play resumes in `inMs`, at match tick `atTick`, into `phase`."
 *
 * Three fields, and each answers a different question:
 *
 * - **`inMs`** is what the 3-2-1 on screen counts down. Every peer starts it
 *   on receipt, so the countdowns are as synchronised as the network allows —
 *   which is the same accuracy `RoundStartMessage.startsInMs` already gets,
 *   and for the same reason it is measured from now rather than named as an
 *   absolute instant: there is no shared clock and this protocol has never
 *   needed one.
 * - **`atTick`** is the *match* tick play picks up on: how many ticks of this
 *   round will have been played when the whistle blows. It is not a board tick
 *   (in a duel the two boards sit at slightly different ones, because the
 *   server deliberately lags each client by whatever the network costs) — it
 *   is the authoritative clock's own number, and it is unchanged across the
 *   pause by construction, because a stopped match buys nobody a tick.
 * - **`phase`** is what is being resumed *into* — `playing`, or the `countdown`
 *   before a round's first tick if the match stopped during one. A client
 *   flips its own loop back on at zero rather than waiting for the `room`
 *   broadcast that says the same thing a round trip later.
 */
export interface ResumeAtMessage {
  readonly round: number;
  /** Milliseconds until play resumes. Clients count this down. */
  readonly inMs: number;
  /** The match tick play resumes on. See above: match, not board. */
  readonly atTick: number;
  /** The phase being resumed into: `playing`, or `countdown`. */
  readonly phase: MatchPhase;
}

/** The heartbeat's answer: the client's own `t`, echoed, plus the server's clock. */
export interface PongMessage {
  readonly t: number;
  readonly serverMs: number;
}

/**
 * How a round finished. Phase 04 had only one way; the variants add two.
 *
 * - `top-out` — somebody's well filled up. Classic duel, `duelSurvival` and
 *   `curses`'s only ending, and still an ending in `duelSprint` ("top-out =
 *   loss") and in `tug` ("or on top-out (opponent wins)").
 * - `goal` — a `duelSprint` seat reached its lines target.
 * - `time-limit` — a `duelSprint` time limit expired and the round went to
 *   whoever had cleared more.
 * - `rope` — `tug`'s rope reached one end.
 * - `forfeit` — the round was awarded against a seat that stopped playing it:
 *   in a friendly match, a stall beyond the round's free stops
 *   (`docs/decisions/0048`). Nothing on the board decided it.
 */
export type RoundEndReason = 'top-out' | 'goal' | 'time-limit' | 'rope' | 'forfeit';

export interface RoundEndMessage {
  readonly round: number;
  /**
   * `null` when the round was drawn and will be replayed: both players topped
   * out in the same server update, both reached the target in the same update,
   * or a time limit expired on equal lines.
   */
  readonly winner: Seat | null;
  readonly scores: readonly number[];
  readonly nextRoundInMs: number | null;
  readonly reason: RoundEndReason;
}

/**
 * Why a match ended. `forfeit` is phase 05's: a ranked grace window ran out,
 * which is a different thing from an opponent choosing to leave and reads
 * differently on the screen that has to explain it.
 *
 * The last two are co-op's, and they exist rather than being folded into
 * `rounds-won` because in co-op **`winner` is always `null`** — nobody beat
 * anybody. A screen handed `{ winner: null, reason: 'rounds-won' }` would have
 * to guess whether that was a draw or a shared victory, and guessing is what
 * this protocol spends its shape avoiding.
 *
 * - `coop-cleared` — the team reached the shared lines target. Both won.
 * - `coop-topped-out` — the shared well filled up. Both lost.
 */
export type MatchEndReason =
  'rounds-won' | 'opponent-left' | 'forfeit' | 'abandoned' | 'coop-cleared' | 'coop-topped-out';

export interface MatchEndMessage {
  readonly winner: Seat | null;
  readonly scores: readonly number[];
  readonly reason: MatchEndReason;
}

export type WarningReason =
  'rate-limit' | 'spectators-cannot-send' | 'unknown-message' | 'wrong-phase' | 'malformed';

export interface WarningMessage {
  readonly reason: WarningReason;
  readonly detail: string;
  readonly strike: number;
  readonly limit: number;
}

export interface RefusedMessage {
  readonly reason: RefusalReason | 'hash-mismatch' | 'rate-limit' | 'grace-expired';
  readonly message: string;
}

/* ------------------------------------------------------------------ *
 * Validation
 * ------------------------------------------------------------------ */

const INPUT_ACTIONS: readonly InputAction[] = [
  'left',
  'right',
  'softDrop',
  'hardDrop',
  'rotateCW',
  'rotateCCW',
  'hold',
];

export function isInputAction(value: unknown): value is InputAction {
  return typeof value === 'string' && (INPUT_ACTIONS as readonly string[]).includes(value);
}

function isNonNegativeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0;
}

/**
 * Validate an `input` message off the wire.
 *
 * Returns the message or `null`; the caller turns `null` into a dropped
 * message and a warning. Nothing partial is ever accepted — a batch with one
 * bad event is a batch from a client that is not running this protocol, and
 * salvaging the rest of it would be guessing.
 */
export function parseInputMessage(raw: unknown, maxEvents: number): InputMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<InputMessage>;
  if (!isNonNegativeInt(candidate.frame)) return null;
  const epoch = parseEpoch(candidate.epoch);
  if (epoch === null) return null;
  if (!Array.isArray(candidate.events)) return null;
  if (candidate.events.length > maxEvents) return null;

  const events: WireInput[] = [];
  let previousFrame = -1;
  for (const entry of candidate.events as readonly unknown[]) {
    if (typeof entry !== 'object' || entry === null) return null;
    const event = entry as Partial<WireInput>;
    if (!isNonNegativeInt(event.frame)) return null;
    if (!isInputAction(event.action)) return null;
    // Frames within a batch must not go backwards, and none may claim a tick
    // later than the batch itself says the client has reached.
    if (event.frame < previousFrame) return null;
    if (event.frame > candidate.frame) return null;
    previousFrame = event.frame;
    events.push({ frame: event.frame, action: event.action });
  }

  return { frame: candidate.frame, events, epoch };
}

/**
 * An absent epoch reads as 0 — the first run of a round, which is what a
 * message sent before any resync belongs to. Anything present but not a
 * non-negative integer is a malformed message, not an old one.
 */
function parseEpoch(value: unknown): Epoch | null {
  if (value === undefined) return 0;
  return isNonNegativeInt(value) ? value : null;
}

export function parseHashMessage(raw: unknown): HashMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<HashMessage>;
  if (!isNonNegativeInt(candidate.piecesPlaced)) return null;
  if (typeof candidate.hash !== 'number' || !Number.isInteger(candidate.hash)) return null;
  const epoch = parseEpoch(candidate.epoch);
  if (epoch === null) return null;
  return { piecesPlaced: candidate.piecesPlaced, hash: candidate.hash >>> 0, epoch };
}

export function parsePauseMessage(raw: unknown): PauseMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<PauseMessage>;
  if (typeof candidate.paused !== 'boolean') return null;
  return { paused: candidate.paused };
}

/**
 * A heartbeat's payload is echoed, not read, so a missing or nonsensical `t`
 * costs nothing and must not cost the client a strike: the message that matters
 * is the one that comes *back*, and refusing to answer a malformed ping would
 * turn a trivial client bug into a disconnection.
 */
export function parsePingMessage(raw: unknown): PingMessage {
  if (typeof raw !== 'object' || raw === null) return { t: 0 };
  const candidate = raw as Partial<PingMessage>;
  return { t: typeof candidate.t === 'number' && Number.isFinite(candidate.t) ? candidate.t : 0 };
}

export function parseReadyMessage(raw: unknown): ReadyMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<ReadyMessage>;
  if (typeof candidate.ready !== 'boolean') return null;
  return { ready: candidate.ready };
}

/**
 * Validate a `loadout` message. `null` for anything that is not exactly
 * `CURSE_LOADOUT_SIZE` distinct, known effects — a client that sent one bad
 * entry gets the whole message dropped, the same "nothing partial is ever
 * accepted" rule `parseInputMessage` states for itself.
 */
export function parseLoadoutMessage(raw: unknown): LoadoutMessage | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const candidate = raw as Partial<{ effects: unknown }>;
  if (!isCurseLoadout(candidate.effects, CURSE_LOADOUT_SIZE)) return null;
  return { effects: candidate.effects };
}
